/**
 * AI Organize lifecycle controller — port of the macOS `NoteOrganizeController`.
 *
 * Framework-agnostic on purpose: the state machine (pin to the note, stop to
 * restore, undo after completion, runId so a stopped stream can never write
 * into the note) is fully unit-testable, and `useOrganize` is a thin
 * `useSyncExternalStore` wrapper around it.
 *
 * Restores are display-only on the Web: commits are suppressed while
 * streaming, so the stored note never left the pre-run state and Stop/undo
 * simply resets the editor back to the original draft.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { organizeStream } from '../api/llm.js';
import { sanitizeTitle, UNTITLED } from '../notes/store.js';

export type OrganizePhase = 'idle' | 'streaming' | 'completed';

export interface OrganizeSnapshot {
  phase: OrganizePhase;
  /** Live status while waiting for the first content token. */
  waitingStatus: string | null;
  errorMessage: string | null;
  /** Accumulated rewrite while streaming; null otherwise. */
  streamedContent: string | null;
  /** Note this run belongs to — never write to another selection. */
  pinnedNoteId: string | null;
  canUndo: boolean;
}

export interface OrganizeDraft {
  title: string;
  content: string;
}

type StreamFn = (
  content: string,
  handlers: {
    onStatus?: (kind: 'waiting' | 'reasoning') => void;
    onContent?: (content: string) => void;
  },
  signal?: AbortSignal,
) => Promise<string>;

const EMPTY_SNAPSHOT: OrganizeSnapshot = {
  phase: 'idle',
  waitingStatus: null,
  errorMessage: null,
  streamedContent: null,
  pinnedNoteId: null,
  canUndo: false,
};

/** Derives a title from the first meaningful line, like the Mac's inference. */
export function inferTitleFromContent(content: string): string {
  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const stripped = trimmed.replace(/^#{1,6}\s*/, '').replace(/[*_`>]+/g, '').trim();
    if (stripped) return sanitizeTitle(stripped.slice(0, 80));
  }
  return UNTITLED;
}

export class OrganizeController {
  private snapshot: OrganizeSnapshot = { ...EMPTY_SNAPSHOT };
  private readonly listeners = new Set<() => void>();
  /** Identifies the active run so a stopped stream cannot write later. */
  private runId = 0;
  private abort: AbortController | null = null;
  private original: OrganizeDraft | null = null;

  constructor(
    private readonly stream: StreamFn,
    /** Persists a finished/undone draft for a note (LibraryScreen.commitForNote). */
    private readonly commit: (noteId: string, draft: OrganizeDraft) => void,
  ) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): OrganizeSnapshot => this.snapshot;

  private emit(patch: Partial<OrganizeSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  get isStreaming(): boolean {
    return this.snapshot.phase === 'streaming';
  }

  /** Starts a rewrite. `draft` must be the flushed, up-to-date editor draft. */
  start = (noteId: string, draft: OrganizeDraft): void => {
    if (this.isStreaming) return;
    if (!draft.content.trim()) {
      this.emit({ ...EMPTY_SNAPSHOT, errorMessage: 'This note is empty.' });
      return;
    }

    this.runId += 1;
    const run = this.runId;
    this.abort = new AbortController();
    this.original = { ...draft };
    this.emit({
      phase: 'streaming',
      waitingStatus: 'Connecting to model…',
      errorMessage: null,
      streamedContent: null,
      pinnedNoteId: noteId,
      canUndo: false,
    });

    void this.run(noteId, draft.content, run);
  };

  private async run(noteId: string, content: string, run: number): Promise<void> {
    // Mac-parity UI throttling: first delta always, then ≥33ms apart or when a
    // big chunk lands, so MarkdownView re-renders never chase every token.
    let appliedFirstDelta = false;
    let lastUIUpdate = 0;
    let lastLength = 0;

    try {
      const final = await this.stream(
        content,
        {
          onStatus: (kind) => {
            if (this.runId !== run || !this.isStreaming) return;
            this.emit({
              waitingStatus: kind === 'reasoning' ? 'Model is thinking…' : 'Waiting for model…',
            });
          },
          onContent: (partial) => {
            if (this.runId !== run || !this.isStreaming) return;
            const now = performance.now();
            if (
              !appliedFirstDelta ||
              now - lastUIUpdate >= 33 ||
              partial.length - lastLength >= 80
            ) {
              appliedFirstDelta = true;
              lastUIUpdate = now;
              lastLength = partial.length;
              this.emit({ waitingStatus: null, streamedContent: partial });
            }
          },
        },
        this.abort?.signal,
      );

      if (this.runId !== run || !this.isStreaming) return;
      const original = this.original ?? { title: UNTITLED, content: '' };
      const wasUntitled = original.title.trim() === '' || original.title === UNTITLED;
      this.commit(noteId, {
        title: wasUntitled ? inferTitleFromContent(final) : original.title,
        content: final,
      });
      this.emit({ phase: 'completed', waitingStatus: null, streamedContent: null, canUndo: true });
    } catch (error) {
      if (this.runId !== run || !this.isStreaming) return;
      if ((error as Error).name === 'AbortError') return; // stop() already reset.
      this.emit({
        ...EMPTY_SNAPSHOT,
        errorMessage:
          error instanceof Error && error.message
            ? error.message
            : 'AI Organize failed. Please try again.',
      });
    }
  }

  /** Stops streaming and puts the editor back to the pre-run draft. */
  stop = (): void => {
    if (!this.isStreaming) return;
    this.runId += 1;
    this.abort?.abort();
    this.abort = null;
    this.original = null;
    this.emit({ ...EMPTY_SNAPSHOT });
  };

  /** After a completed run, restores the previous note body and title. */
  undo = (): void => {
    const { phase, pinnedNoteId } = this.snapshot;
    if (phase !== 'completed' || !pinnedNoteId || !this.original) return;
    this.commit(pinnedNoteId, this.original);
    this.abort = null;
    this.original = null;
    this.emit({ ...EMPTY_SNAPSHOT });
  };

  dismissError = (): void => {
    if (this.snapshot.errorMessage !== null) this.emit({ errorMessage: null });
  };

  /**
   * Selection changed away from the pinned note: a streaming run is stopped
   * (matching the Mac), a completed run drops its undo affordance.
   */
  noteSwitched = (nextNoteId: string | null): void => {
    const { phase, pinnedNoteId } = this.snapshot;
    if (pinnedNoteId === null || nextNoteId === pinnedNoteId) return;
    if (phase === 'streaming') this.stop();
    else if (phase === 'completed') {
      this.abort = null;
      this.original = null;
      this.emit({ ...EMPTY_SNAPSHOT });
    }
  };

  dispose = (): void => {
    if (this.isStreaming) this.stop();
  };
}

/**
 * React binding for the organize controller.
 *
 * `commit` is kept in a ref so the controller always calls the latest
 * LibraryScreen closure without re-subscribing.
 */
export function useOrganize(
  commit: (noteId: string, draft: OrganizeDraft) => void,
): OrganizeController {
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const controllerRef = useRef<OrganizeController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new OrganizeController(organizeStream, (noteId, draft) =>
      commitRef.current(noteId, draft),
    );
  }
  const controller = controllerRef.current;

  useEffect(() => controller.dispose, [controller]);

  useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);

  return controller;
}
